import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import U2000SaveCancelDialog from '../../../dialog/U2000SaveCancelDialog';
import U2000InputTextBase from '../../../formControls/input/U2000InputTextBase';
import U2000Alert from '../../../controls/U2000Alert';
import U2000CheckBoxBase from '../../../formControls/checkbox/U2000CheckBoxBase';

export type U2000SearchFilterSaveMode = 'create' | 'update';

export interface U2000SearchFilterSaveResult {
  filterName: string;
  isDefaultFilter: boolean;
  makeDatesDynamic: boolean;
}

interface U2000SearchFilterSaveProps {
  isDialogOpen: boolean;
  defaultFilter: boolean;
  hasDateCriteria: boolean;
  mode?: U2000SearchFilterSaveMode;
  initialFilterName?: string;
  setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: (result: U2000SearchFilterSaveResult) => void;
}

const U2000SearchFilterSave: React.FC<U2000SearchFilterSaveProps> = ({
  isDialogOpen,
  defaultFilter,
  hasDateCriteria,
  mode = 'create',
  initialFilterName = '',
  setDialogOpen,
  onClose,
}) => {
  const { t } = useTranslation('common');

  const [filterName, setFilterName] = useState('');
  const [isDefaultFilter, setIsDefaultFilter] = useState(false);
  const [makeDatesDynamic, setMakeDatesDynamic] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const userRef = useRef<HTMLInputElement>(null);
  const isUpdateMode = mode === 'update';

  function onCloseDlg(_action: 'save' | 'cancel'): void {
    if (_action === 'save') {
      const resolvedName = (isUpdateMode ? initialFilterName : filterName).trim();
      if (!isUpdateMode && resolvedName === '') {
        setShowAlert(true);
        userRef.current?.focus();
        return;
      }
      setShowAlert(false);
      if (setDialogOpen) setDialogOpen(false);
      onClose({
        filterName: resolvedName,
        isDefaultFilter: isUpdateMode ? defaultFilter : isDefaultFilter,
        makeDatesDynamic: hasDateCriteria ? makeDatesDynamic : false,
      });
    } else if (_action === 'cancel') {
      if (setDialogOpen) setDialogOpen(false);
    }
  }

  useEffect(() => {
    setIsDefaultFilter(defaultFilter);
  }, [defaultFilter]);

  useEffect(() => {
    if (isDialogOpen) {
      setFilterName(isUpdateMode ? initialFilterName : '');
      setMakeDatesDynamic(false);
      setShowAlert(false);
      if (!isUpdateMode) {
        setTimeout(() => {
          userRef.current?.focus();
        }, 100);
      }
    }
  }, [isDialogOpen, isUpdateMode, initialFilterName]);

  const dialogTitle = isUpdateMode
    ? t('dlgSearchFilterUpdateTitle', { name: initialFilterName })
    : t('dlgLblFilterName');

  return (
    <U2000SaveCancelDialog isOpen={isDialogOpen} onClose={onCloseDlg} title={dialogTitle} width="medium">
      {showAlert && (
        <U2000Alert
          message={t('Veuillez entrer un nom de filtre.')}
          type="error"
          onClose={() => setShowAlert(false)}
          isOpen={false}
        />
      )}
      {!isUpdateMode && (
        <>
          <U2000InputTextBase
            fieldName={filterName}
            type="string"
            value={filterName}
            setValue={setFilterName as (value: string | number) => void}
            inputRef={userRef}
          />
          <U2000CheckBoxBase
            fieldName="isDefaultFilter"
            label={t('Définir comme filtre par défaut')}
            value={isDefaultFilter}
            setValue={setIsDefaultFilter}
            className="mt-2"
          />
        </>
      )}
      {isUpdateMode && (
        <p className="text-sm text-gray-600">{t('dlgSearchFilterUpdateHint', { name: initialFilterName })}</p>
      )}
      {hasDateCriteria && (
        <div className={`${isUpdateMode ? 'mt-3' : 'mt-4'} border-t border-gray-200 pt-4`}>
          <p className="mb-2 text-sm font-medium text-gray-800">{t('dlgSearchFilterMakeDatesDynamicTitle')}</p>
          <p className="mb-3 text-sm text-gray-600">{t('dlgSearchFilterMakeDatesDynamicMessage')}</p>
          <U2000CheckBoxBase
            fieldName="makeDatesDynamic"
            label={t('dlgSearchFilterMakeDatesDynamicYes')}
            value={makeDatesDynamic}
            setValue={setMakeDatesDynamic}
          />
          {!makeDatesDynamic && (
            <p className="mt-1 text-xs text-gray-500">{t('dlgSearchFilterMakeDatesDynamicNo')}</p>
          )}
        </div>
      )}
    </U2000SaveCancelDialog>
  );
};

export default U2000SearchFilterSave;
